import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  MenuItem,
  Checkbox,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import { useFetchTiers, useCreateTier, useUpdateTier, useDeleteTier } from '@/shared/actions/erp/comisiones/comisiones.actions';
import { useFetchCitiesEnabled } from '@/shared/actions/erp';
import LoadingDialog from './LoadingDialog';
import type { CommissionTier } from '@/lib/commissions/types';
import { TIERS_PREDEFINIDOS } from '@/lib/commissions/constants';

interface TiersFormProps {
  onSaved: () => void;
}

type TierFormState = Omit<CommissionTier, 'ciudad'> & { ciudad: string[] };

const buildTierFormState = (tier?: CommissionTier): TierFormState => ({
  id: tier?.id,
  rol: tier?.rol ?? 'asesor',
  desde: tier?.desde ?? 1,
  hasta: tier?.hasta ?? 10,
  factor: tier?.factor ?? 0,
  ciudad:
    tier?.ciudad && tier.ciudad !== 'null'
      ? tier.ciudad.split(',').map((c) => c.trim().toUpperCase())
      : [],
});

export default function TiersForm({ onSaved }: TiersFormProps) {
  const { data: tiers, isLoading } = useFetchTiers();
  const createMutation = useCreateTier({ enableSuccessAlert: true });
  const updateMutation = useUpdateTier({ enableSuccessAlert: true });
  const deleteMutation = useDeleteTier();

  const { data: citiesResponse, isLoading: isLoadingCities } = useFetchCitiesEnabled({
    params: { page_size: 1000, estado: true }
  });

  const ciudades = citiesResponse?.data?.data || [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<TierFormState | null>(null);
  const [searchText, setSearchText] = useState('');
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const updateEditingTier = (updates: Partial<TierFormState>) => {
    setEditingTier((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  const handleCiudadChange = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    setEditingTier((prev) => {
      if (!prev) return prev;
      const value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

      if (value.includes('TODAS')) {
        return {
          ...prev,
          ciudad: prev.ciudad.includes('TODAS') ? [] : ['TODAS'],
        };
      }

      return {
        ...prev,
        ciudad: value.filter((v) => v !== 'TODAS'),
      };
    });
  };

  const handleOpenDialog = (tier?: CommissionTier) => {
    setEditingTier(buildTierFormState(tier));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTier(null);
    setSearchText('');
  };

  const handleSave = async () => {
    if (!editingTier) return;

    try {
      const { ciudad, ...rest } = editingTier;
      const tierData: Partial<CommissionTier> = {
        ...rest,
        ciudad: ciudad.length === 0 || ciudad.includes('TODAS') ? null : ciudad.join(','),
      };

      if (editingTier.id) {
        await updateMutation.mutateAsync(tierData as CommissionTier);
      } else {
        await createMutation.mutateAsync(tierData);
      }
      onSaved();
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar tier:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este tier?')) {
      await deleteMutation.mutateAsync(id);
      onSaved();
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los tiers a los valores predefinidos? Esto eliminará todos los tiers existentes y creará nuevos con los valores predeterminados.')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps = (tiers?.length || 0) + TIERS_PREDEFINIDOS.length;
      let currentStep = 0;

      // Eliminar todos los tiers existentes
      if (tiers && tiers.length > 0) {
        for (const tier of tiers) {
          if (tier.id) {
            await deleteMutation.mutateAsync(tier.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear los tiers predefinidos
      for (const tier of TIERS_PREDEFINIDOS) {
        await createMutation.mutateAsync(tier);
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    } catch (error) {
      console.error('Error al restablecer tiers:', error);
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  const filteredCiudades = useMemo(() => {
    const uniqueCiudades = ciudades.reduce((acc: any[], ciudad: any) => {
      const nombreNormalizado = ciudad.nombre.toLowerCase();
      if (!acc.some(c => c.nombre.toLowerCase() === nombreNormalizado)) {
        acc.push(ciudad);
      }
      return acc;
    }, []);

    if (!searchText) return uniqueCiudades;
    const searchLower = searchText.toLowerCase();
    return uniqueCiudades.filter((ciudad: any) =>
      ciudad.nombre.toLowerCase().includes(searchLower)
    );
  }, [ciudades, searchText]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Tiers de Comisión</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestablecer}
            size="small"
            color="warning"
            disabled={createMutation.isPending || deleteMutation.isPending}
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nuevo Tier
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rol</TableCell>
              <TableCell align="right">Desde</TableCell>
              <TableCell align="right">Hasta</TableCell>
              <TableCell align="right">Factor (%)</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers && tiers.length > 0 ? (
              tiers.map((tier) => (
                <TableRow key={tier.id}>
                  <TableCell>{tier.rol}</TableCell>
                  <TableCell align="right">{tier.desde}</TableCell>
                  <TableCell align="right">{tier.hasta}</TableCell>
                  <TableCell align="right">{(Number(tier.factor) * 100).toFixed(0)}%</TableCell>
                  <TableCell>
                    {!tier.ciudad || tier.ciudad === 'null'
                      ? 'Todas'
                      : tier.ciudad === 'TODAS'
                        ? 'Todas'
                        : tier.ciudad.split(',').map(c => c.trim().charAt(0).toUpperCase() + c.trim().slice(1).toLowerCase()).join(', ')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(tier)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => tier.id && handleDelete(tier.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No hay tiers registrados
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de edición */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTier?.id ? 'Editar Tier' : 'Nuevo Tier'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              label="Rol"
              value={editingTier?.rol || 'asesor'}
              onChange={(e) => updateEditingTier({ rol: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="asesor">Asesor</MenuItem>
              <MenuItem value="vendedor">Vendedor</MenuItem>
              <MenuItem value="supervisor">Supervisor</MenuItem>
            </TextField>
            <TextField
              label="Desde"
              type="number"
              value={editingTier?.desde || 1}
              onChange={(e) => updateEditingTier({ desde: parseInt(e.target.value, 10) })}
              fullWidth
              required
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Hasta"
              type="number"
              value={editingTier?.hasta || 10}
              onChange={(e) => updateEditingTier({ hasta: parseInt(e.target.value, 10) })}
              fullWidth
              required
              inputProps={{ min: 0 }}
            />
            <TextField
              label="Factor (%)"
              type="number"
              value={editingTier?.factor ? editingTier.factor * 100 : 0}
              onChange={(e) => updateEditingTier({ factor: parseFloat(e.target.value) / 100 })}
              fullWidth
              required
              inputProps={{ step: 1, min: 0, max: 100 }}
              helperText="Porcentaje del margen (ej: 20 para 20%)"
            />
            <FormControl fullWidth>
              <InputLabel id="ciudad-select-label">Ciudad</InputLabel>
              <Select
                labelId="ciudad-select-label"
                multiple
                value={editingTier?.ciudad ?? []}
                onChange={handleCiudadChange}
                onClose={() => setSearchText('')}
                input={<OutlinedInput label="Ciudad" />}
                renderValue={(selected: string[]) => {
                  if (selected.length === 0) return 'Ninguna seleccionada';
                  if (selected.includes('TODAS')) return 'Todas las ciudades';
                  return selected.join(', ');
                }}
                disabled={isLoadingCities}
                MenuProps={{
                  autoFocus: false,
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        py: 0.5,
                      }
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar ciudad..."
                    fullWidth
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Box>
                <MenuItem value="TODAS">
                  <Checkbox checked={(editingTier?.ciudad ?? []).includes('TODAS')} />
                  <ListItemText primary="Todas las ciudades" />
                </MenuItem>
                {filteredCiudades.map((ciudad: any) => {
                  const nombreNormalizado = ciudad.nombre.charAt(0).toUpperCase() + ciudad.nombre.slice(1).toLowerCase();
                  const ciudadMayusculas = ciudad.nombre.toUpperCase();
                  return (
                    <MenuItem
                      key={`tier-ciudad-${ciudad.id}`}
                      value={ciudadMayusculas}
                      disabled={(editingTier?.ciudad ?? []).includes('TODAS')}
                    >
                      <Checkbox checked={(editingTier?.ciudad ?? []).includes(ciudadMayusculas)} />
                      <ListItemText primary={nombreNormalizado} />
                    </MenuItem>
                  );
                })}
                {filteredCiudades.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="No se encontraron ciudades" />
                  </MenuItem>
                )}
              </Select>
              <Typography variant="caption" sx={{ mt: 0.5, ml: 1.75, color: 'text.secondary' }}>
                Selecciona "Todas" o una o varias ciudades específicas
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de carga */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo tiers..."
        message="Eliminando tiers actuales y creando los valores predefinidos."
      />
    </Box>
  );
}

